import React from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { toast } from '@cotera/client/app/components/ui/toaster';

export const BillEngineeringHoursPage: React.FC = () => {
  const client = useUntenantedClient();

  const [orgId, setOrgId] = React.useState('');
  const [hours, setHours] = React.useState('');
  const [externalNote, setExternalNote] = React.useState('');
  const [internalNote, setInternalNote] = React.useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await client.superAdminBilling.billEngineeringHours({
        orgId,
        hours: parseFloat(hours),
        externalNote,
        internalNote: internalNote.length > 0 ? internalNote : undefined,
      });

      if (res.isErr()) {
        const error = res.error;
        if (error.t === 'application') {
          throw new Error(error.err.errorType);
        }
        throw new Error('Request failed');
      }

      return res.value;
    },
    onSuccess: () => {
      toast.success('Engineering hours billed successfully');
      setOrgId('');
      setHours('');
      setExternalNote('');
      setInternalNote('');
    },
    onError: (err: Error) => {
      toast.error(`Failed to bill engineering hours: ${err.message}`);
    },
  });

  const canSubmit =
    orgId.length > 0 &&
    hours.length > 0 &&
    !isNaN(parseFloat(hours)) &&
    parseFloat(hours) > 0 &&
    externalNote.length > 0 &&
    !mutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) {
      mutation.mutate();
    }
  };

  return (
    <div className="flex flex-col h-full p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Bill Engineering Hours</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bill engineering hours to an organization via Orb
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>New Billing Entry</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="orgId">Organization ID</Label>
              <Input
                id="orgId"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                placeholder="Enter organization ID"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hours">Hours</Label>
              <Input
                id="hours"
                type="number"
                step="0.25"
                min="0"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="e.g. 2.5"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="externalNote">External Note</Label>
              <Input
                id="externalNote"
                value={externalNote}
                onChange={(e) => setExternalNote(e.target.value)}
                placeholder="Note visible to the customer"
              />
              <p className="text-xs text-muted-foreground">
                Used as part of the idempotency key — duplicate external notes
                on the same day for the same org will be rejected.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="internalNote">Internal Note (optional)</Label>
              <Textarea
                id="internalNote"
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder="Internal-only note"
                rows={3}
              />
            </div>

            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? 'Submitting...' : 'Bill Hours'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
